import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@radix-ui/react-icons';
import { Text, UnstyledButton } from '@/shared/ui';
import type { GroupAggregate, PropertyGroup } from '../../pages/property-grouping';
import { GroupSplitBar } from './group-split-bar';
import { formatArea, formatMoney, portfolioShare } from './group-metrics';

interface GroupSummaryCardProps {
  /** The group this card summarizes. */
  group: PropertyGroup;
  /** The group's aggregate stats, or null while pending. */
  aggregate: GroupAggregate | null;
  /** The portfolio total, for the share percentage. */
  total: number;
  /** Whether the split bar should be shown (hidden when grouping by business). */
  showSplit: boolean;
  /** Applies this group's filter and returns to the list view. */
  onSelect: () => void;
}

/** One labelled metric cell in the 2×2 grid. */
const Metric: React.FC<{ label: string; value: string; suffix?: string }> = ({ label, value, suffix }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
    <Text
      as="span"
      style={{
        fontSize: 'var(--text-tag)',
        letterSpacing: 'var(--tracking-chip, 0.06em)',
        textTransform: 'uppercase',
        fontWeight: 'var(--font-semibold)' as unknown as number,
        color: 'var(--text-tertiary)',
      }}
    >
      {label}
    </Text>
    <Text
      as="span"
      style={{
        fontSize: 'var(--text-body-sm)',
        fontWeight: 'var(--font-semibold)' as unknown as number,
        color: 'var(--text-primary)',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
      {suffix && <Text component="span" style={{ fontSize: 'var(--text-meta)', fontWeight: 500, color: 'var(--text-secondary)' }}>{suffix}</Text>}
    </Text>
  </div>
);

/**
 * One city/group as a Resumo card: name + UF, its share of the portfolio, the
 * count, a sale/rental/both split bar, a 2×2 metric grid, and a "Ver imóveis →"
 * footer. The whole card is a button — activating it applies the group's
 * filter and switches back to the list view.
 *
 * @param props - The group, its aggregate, the portfolio total, and handlers.
 * @returns The summary card element.
 */
export const GroupSummaryCard: React.FC<GroupSummaryCardProps> = ({ group, aggregate, total, showSplit, onSelect }) => {
  const { t } = useTranslation('features/properties');
  const count = aggregate?.count ?? group.items.length;
  const share = portfolioShare(count, total);

  return (
    <UnstyledButton
      type="button"
      data-testid="group-summary-card"
      onClick={onSelect}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        textAlign: 'left',
        width: '100%',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-primary)',
        boxShadow: 'var(--shadow-sm)',
        cursor: 'pointer',
        fontFamily: 'var(--font-family)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', minWidth: 0 }}>
          <Text
            as="span"
            style={{
              fontSize: 'var(--text-app-heading)',
              fontWeight: 'var(--font-semibold)' as unknown as number,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {group.label}
          </Text>
          {group.subLabel && (
            <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>{group.subLabel}</Text>
          )}
        </div>
        <Text as="span" style={{ fontSize: 'var(--text-stat)', fontWeight: 'var(--font-bold)' as unknown as number, color: 'var(--seed-base)', fontVariantNumeric: 'tabular-nums' }}>
          {count}
        </Text>
      </div>

      <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
        {t('list.grouping.shareOfPortfolio', { percent: share })}
      </Text>

      {showSplit && aggregate && <GroupSplitBar split={aggregate.byBusinessType} variant="bar" />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Metric label={t('list.grouping.metricMedianSale')} value={formatMoney(aggregate?.medianSalePrice ?? null)} />
        <Metric
          label={t('list.grouping.metricMedianRental')}
          value={formatMoney(aggregate?.medianRentalPrice ?? null)}
          suffix={aggregate?.medianRentalPrice != null ? t('list.perMonth') : undefined}
        />
        <Metric label={t('list.grouping.metricPricePerM2')} value={formatMoney(aggregate?.avgPricePerM2 ?? null)} />
        <Metric label={t('list.grouping.metricAvgArea')} value={formatArea(aggregate?.avgTotalArea ?? null)} />
      </div>

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', color: 'var(--seed-base)', fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-semibold)' as unknown as number }}>
        {t('list.grouping.seeProperties')}
        <ArrowRightIcon width={15} height={15} />
      </div>
    </UnstyledButton>
  );
};
